import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Star, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

/**
 * SplashHollerStrip — punchy band on the splash page that teases "The Holler",
 * the community testimonial wall. Shows up to 3 highlight quotes and CTAs over
 * to the full /holler page.
 *
 * "Holler" is Appalachian for both a small valley AND "speak up" — perfect
 * regional flavor for the Tri-Cities (Bristol/Kingsport/Johnson City).
 */
export default function SplashHollerStrip() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/holler", { params: { limit: 3 } }).then(({ data }) => {
      if (cancelled) return;
      setItems(data.items || []);
      setTotal(data.total || 0);
      setEnabled(data.enabled !== false);
    }).catch(() => { /* silent — strip is non-critical */ });
    return () => { cancelled = true; };
  }, []);

  if (!enabled) return null;

  return (
    <section
      className="splash-holler-strip py-14 md:py-20 px-5 relative overflow-hidden"
      data-testid="splash-holler-strip"
    >
      {/* Subtle grid background */}
      <div aria-hidden className="splash-holler-grid" />

      <div className="splash-shell relative">
        <div className="grid md:grid-cols-12 gap-8 items-start">
          {/* Left — copy + CTA */}
          <div className="md:col-span-5 lg:col-span-4">
            <div className="splash-overline">From the holler</div>
            <h2 className="splash-display text-5xl md:text-6xl leading-[0.9] mt-3">
              Word
              <br />
              from the
              <br />
              <span style={{ color: "#76B82A" }}>trail.</span>
            </h2>
            <p className="splash-mono mt-5 text-sm text-[#0d100d]/80 max-w-md">
              In Appalachia, a "holler" is a small valley — and "give us a holler"
              means speak up. Win or skunked, hot trail or cold tracks: this is where
              the community brags, vents, and leaves their mark.
            </p>
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <Link
                to="/holler"
                className="btn-loot inline-flex items-center gap-2"
                data-testid="splash-holler-visit"
              >
                <MessageSquare size={14} /> Visit the Holler
                <ArrowRight size={14} />
              </Link>
              <span className="splash-mono uppercase tracking-widest text-[10px] text-[#0d100d]/55">
                {total > 0 ? `${total} ${total === 1 ? "voice" : "voices"} & counting` : "Be the first to holler"}
              </span>
            </div>
          </div>

          {/* Right — quote tiles */}
          <div className="md:col-span-7 lg:col-span-8">
            {items.length === 0 ? (
              <EmptyTeaser />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {items.map((t, i) => <QuoteTile key={t.id} t={t} idx={i} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function QuoteTile({ t, idx }) {
  // Slight rotational variety for visual interest
  const rotations = ["-rotate-1", "rotate-0", "rotate-1"];
  return (
    <Link
      to="/holler"
      className={`splash-holler-tile block ${rotations[idx % 3]} transition-transform hover:rotate-0 hover:scale-[1.02]`}
      data-testid={`splash-holler-tile-${t.id}`}
    >
      {t.rating && (
        <div className="flex items-center gap-0.5 mb-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Star
              key={i}
              size={11}
              fill={i <= t.rating ? "#D7A62A" : "transparent"}
              stroke={i <= t.rating ? "#D7A62A" : "rgba(13,16,13,0.35)"}
            />
          ))}
        </div>
      )}
      <p className="text-[14px] leading-relaxed text-[#0d100d]/90 line-clamp-5">
        “{t.body}”
      </p>
      <div className="mt-3 pt-3 border-t border-[#0d100d]/10 flex items-center justify-between gap-2">
        <span className="splash-display uppercase text-sm">{t.name}</span>
        {t.city && (
          <span className="splash-mono text-[10px] text-[#0d100d]/55">{t.city}</span>
        )}
      </div>
    </Link>
  );
}

function EmptyTeaser() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div className="splash-holler-tile">
        <p className="text-[14px] leading-relaxed text-[#0d100d]/85 italic">
          “Spent two hours hunting in the rain, came up empty, and laughed the
          whole time. Already booked next weekend's hunt. 10/10.”
        </p>
        <div className="mt-3 pt-3 border-t border-[#0d100d]/10 flex items-center justify-between gap-2">
          <span className="splash-display uppercase text-sm">@hunter (sample)</span>
          <span className="splash-mono text-[10px] text-[#0d100d]/55">Bristol</span>
        </div>
      </div>
      <div className="hidden sm:flex items-center justify-center splash-mono uppercase tracking-widest text-[10px] text-[#0d100d]/45 italic text-center px-4">
        ↖ This spot is yours.
        <br />
        Real hollers from real hunters drop in here.
      </div>
    </div>
  );
}
